import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { ControlContainer, FormsModule, NgForm, NgModelGroup } from '@angular/forms';
import { IPersonnelGroup, ISelectOption } from 'app/models/filter-form.models';
import { IAutocompleteOption } from 'app/models/filter.models';
import { AuthService } from 'app/services/auth.service';
import { DepartmentService } from 'app/services/department.service';
import { FilterService } from 'app/services/filter.service';
import { makeControlDirty, mapValuesToOptions } from 'app/utils/form.utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgFor, NgIf } from '@angular/common';
import { FilterItemComponent } from '../filter-item/filter-item.component';
import { MatFormField } from '@angular/material/form-field';
import { MatOption } from '@angular/material/autocomplete';
import { MatSelect } from '@angular/material/select';
import { MultiSelectWithSearchComponent } from '../../form/multiselect-with-search/multiselect-with-search.component';

@Component({
  selector: 'app-personnel-tab',
  templateUrl: './personnel-tab.component.html',
  styleUrls: ['./personnel-tab.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
  imports: [
    FormsModule,
    NgIf,
    FilterItemComponent,
    MatFormField,
    MatSelect,
    NgFor,
    MatOption,
    MultiSelectWithSearchComponent,
  ],
})
export class PersonnelTabComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  @ViewChild('personnel') ngModelGroup!: NgModelGroup;

  departmentId = '';
  personnelGroup: IPersonnelGroup = {
    departmentId: undefined,
    officers: undefined,
    units: undefined,
  };

  departmentOptions: ISelectOption[] = [];
  officerNameOptions: IAutocompleteOption[] = [];

  get departmentValue(): string | undefined {
    const name = this.departmentOptions.find(({ value }) => value === this.personnelGroup.departmentId)?.viewValue;

    return this.isAdmin ? name : undefined;
  }

  get officerNamesValue() {
    return this.personnelGroup.officers?.map((x) => x.value).join(', ') ?? undefined;
  }

  get unitsValue() {
    return this.personnelGroup.units?.map((x) => x.value).join(', ') ?? undefined;
  }

  get isAdmin(): boolean {
    return this.authService.isAdmin;
  }

  constructor(
    private readonly filterService: FilterService,
    private readonly authService: AuthService,
    private readonly departmentService: DepartmentService,
  ) {}

  ngOnInit(): void {
    if (this.isAdmin) {
      this.departmentService
        .getDepartments()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((departments) => {
          this.departmentOptions = departments.map(({ id, name }) => ({
            value: id,
            viewValue: name,
          }));
        });

      this.departmentService.departmentId$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((departmentId) => {
        this.departmentId = departmentId;
      });
    }

    this.updateOfficerAndUnitOptions();

    const { appliedFilters: filters } = this.filterService;

    if (!filters.personnelFilter) return;

    this.personnelGroup = {
      departmentId: filters.personnelFilter?.departmentId,
      officers: filters.personnelFilter?.officers?.map(({ keycloakId, fullName }) => ({
        id: keycloakId,
        value: fullName,
      })),
      units: mapValuesToOptions(filters.personnelFilter?.units),
    };
  }

  updateOfficerAndUnitOptions() {
    this.filterService
      .getPersonnel(this.isAdmin, this.departmentId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((officers) => {
        this.officerNameOptions = officers.map((officer) => ({
          id: officer.keycloakId,
          value: [officer.officerName, '#' + officer.officerNumber].filter(Boolean).join(' '),
        }));
      });
  }

  onDepartmentChange(id: string) {
    if (!this.isAdmin) return;
    this.personnelGroup.departmentId = id;
    this.departmentService.departmentId$.next(id);

    this.personnelGroup.officers = undefined;
    this.personnelGroup.units = undefined;
    this.updateOfficerAndUnitOptions();
  }

  clearFilter(filter: 'departmentId' | 'officers' | 'units'): () => void {
    return () => {
      makeControlDirty(this.ngModelGroup.control);

      switch (filter) {
        case 'departmentId':
          this.personnelGroup.departmentId = undefined;
          this.departmentId = '';
          this.updateOfficerAndUnitOptions();
          break;
        case 'officers':
          this.personnelGroup.officers = undefined;
          break;
        case 'units':
          this.personnelGroup.units = undefined;
          break;
      }
    };
  }
}
